import { ChangeDetectionStrategy, Component, computed, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import {
  backNavigation,
  requestFn,
  scan,
  Scan,
  ScannerStore,
  SOFTLINE_FEATURE_SCANNER,
  title,
} from '@softline/application';
import { Store } from '@softline/core';
import { map } from 'rxjs';
import { SOFTLINE_FEATURE_DELIVERY_NOTE } from '../../delivery-note.shared';
import { DeliveryNotesStore } from '../../stores';
import { PalettenModus } from '../../types/delivery-note';
import { Auslieferungsstatus } from '../../types/palette';
import { TranslatePipe, IconComponent, ButtonComponent, modalFn, CardComponent, CardBodyComponent, BadgeComponent } from '@softline/ui-core';
import { DsProgressBarComponent } from '@softline/ui-core/progress';
import { PalettModusChangeDialogComponent } from '../../dialogs/palett-modus-change-dialog/palett-modus-change-dialog.component';
import { PageComponent, PageFooterComponent } from '@softapps/core/pages';
import { DeliveryNoteService } from '../../services/delivery-note.service';

@Component({
  selector: 'soft-palett-scan',
  imports: [
    TranslatePipe,
    IconComponent,
    ButtonComponent,
    PageComponent,
    PageFooterComponent,
    CardComponent,
    CardBodyComponent,
    BadgeComponent,
    DsProgressBarComponent,
  ],
  templateUrl: './palett-scan.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PalettScan {
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly store = inject(Store);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly deliveryNoteService = inject(DeliveryNoteService);

  readonly id = toSignal(this.activatedRoute.paramMap.pipe(map((p) => p.get('id'))));

  protected readonly PalettenModus = PalettenModus;
  protected readonly Auslieferungsstatus = Auslieferungsstatus;

  request = requestFn();
  modal = modalFn();

  constructor() {
    title('Paletten scannen');
    backNavigation(() => '/lieferscheine/' + this.id());

    scan(async (scan: Scan) => {
      const item = this.item();
      if (!item || !scan.data || item.palettenModus === PalettenModus.Erledigt) return;

      const updatedNote = await this.request({
        request: () =>
          this.deliveryNoteService.scanPalett(item.id, scan.data, item.palettenModus),
        options: { type: 'blocking' },
      });

      if (!updatedNote) return;

      this.store.commit(
        SOFTLINE_FEATURE_DELIVERY_NOTE,
        DeliveryNotesStore.mutations.collection.addOrUpdate,
        updatedNote
      );

      await this.checkPalettenModus(item.palettenModus, updatedNote.palettenModus);
    });
  }

  readonly item = this.store.signal(
    SOFTLINE_FEATURE_DELIVERY_NOTE,
    DeliveryNotesStore.getters.collection.preparedEntity,
    computed(() => +(this.id() ?? 0)),
    { initialValue: null }
  );

  protected readonly palettenModus = computed(() => {
    const item = this.item();
    if (!item) return;
    return item.palettenModus as string;
  });

  protected readonly numOfErledigtPaletten = computed(() => {
    const paletten = this.item()?.paletten;
    const modus = this.item()?.palettenModus;
    if (!paletten || !modus) return;
    switch (modus) {
      case PalettenModus.Erledigt:
        return paletten.length;
      case PalettenModus.Beladen:
        return paletten.filter(
          (p) => p.auslieferungsstatus === Auslieferungsstatus.Beladen
        ).length;
      case PalettenModus.Entladen:
        return paletten.filter(
          (p) => p.auslieferungsstatus === Auslieferungsstatus.Entladen
        ).length;
      default:
        return 0;
    }
  });

  protected readonly progressBarValue = computed(() => {
    const numOfPaletten = this.item()?.paletten.length;
    const numOfErledigtPaletten = this.numOfErledigtPaletten();
    if (!numOfPaletten || !numOfErledigtPaletten) return 0;
    return (numOfPaletten / numOfErledigtPaletten) * 100;
  });

  protected readonly navigate = async (palettId: number) =>
    await this.router.navigate(['palett-detail', palettId], { relativeTo: this.route });

  protected readonly navigateToDeliveryNote = async () =>
    await this.router.navigate(['/lieferscheine', this.id()]);

  protected readonly startCamera = async () => {
    await this.store.dispatch(SOFTLINE_FEATURE_SCANNER, ScannerStore.actions.scan, {});
  };

  private async checkPalettenModus(
    previousModus: PalettenModus,
    nextModus: PalettenModus
  ): Promise<void> {
    if (previousModus === nextModus) return;

    let message: string | null = null;

    if (previousModus === PalettenModus.Beladen && nextModus === PalettenModus.Entladen)
      message = '#DELIVERY_NOTE.DIALOGS.PALETT_MODUS_CHANGE.BELADEN_DONE';
    else if (
      previousModus === PalettenModus.Entladen &&
      nextModus === PalettenModus.Erledigt
    )
      message = '#DELIVERY_NOTE.DIALOGS.PALETT_MODUS_CHANGE.ENTLADEN_DONE';

    if (message) {
      await this.modal({
        component: PalettModusChangeDialogComponent,
        data: { message },
        dismiss: false,
      });
      await this.router.navigate(['/lieferscheine', this.id()]);
    }
  }
}
